import React, { useEffect, useMemo, useState } from 'react';

import { AnchorLinkType } from 'components/anchorLink';
import { IconsEnum } from 'components/Icons';
import { PokerButtonGame } from 'components/poker/pokerButtonGame';
import Script from 'next/script';
import { Wrapper } from '../../wrapper';
import { callbackOnAuthenticated } from 'utility/auth';
import { getNonce } from 'lib/policy';
import { launchGamePoker } from 'lib/launchGame/utils';
import styles from './PokerActionBar.module.scss';
import useAgentDetect from 'hooks/useAgentDetect';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { useInternalSession } from 'hooks/useInternalSession';
import { isMatch } from 'utility/functions';
import useWindowSize from 'hooks/useWindowSize';

export type PokerActionBarProps = {
  linkForWindows: AnchorLinkType[];
  linkForAndroid: AnchorLinkType[];
  linkForIos: AnchorLinkType[];
  frameDimension?: {
    width: number;
    height: number;
  };
};

export const PokerActionBar = ({ linkForWindows, linkForAndroid, linkForIos, frameDimension }: PokerActionBarProps) => {
  const [desktopLink, setDesktopLink] = useState<AnchorLinkType[] | undefined>();
  const [injectScript, setInjectScript] = useState<boolean>(false);
  /* hooks */
  const { isMac, isIos } = useAgentDetect();
  const { isMobile } = useWindowSize();

  const { session, isAuthenticated } = useInternalSession();
  /* labels */
  const labelPlayOnline = useGetLabelByKey('gioca-online')!;
  const labelAppDsk = useGetLabelByKey('app-desktop')!;
  const labelAppMob = useGetLabelByKey('app-mobile')!;

  useEffect(() => {
    setDesktopLink(isMac ? linkForIos : linkForWindows);
  }, [isMac, linkForIos, linkForWindows]);

  const desktopCondition = useMemo(() => {
    return (linkForIos || linkForWindows) && !isMobile && desktopLink;
  }, [linkForIos, linkForWindows, isMobile, desktopLink]);

  return (
    <React.Fragment>
      {injectScript && isMatch(process.env.NEXT_PUBLIC_SCRIPT_URL_POKER, '^http') && (
        <Script src={process.env.NEXT_PUBLIC_SCRIPT_URL_POKER} nonce={getNonce()}></Script>
      )}
      <Wrapper noPaddingBottom={true} noPaddingTop={true}>
        <div className={styles.container}>
          <PokerButtonGame
            handleOnClick={() => {
              setInjectScript(true);
              callbackOnAuthenticated(isAuthenticated, () =>
                launchGamePoker(session?.user?.cardNumber!, frameDimension)
              );
            }}
            iconName={IconsEnum.STADIA_CONTROLLER}
          >
            {labelPlayOnline}
          </PokerButtonGame>
          {desktopCondition && (
            <PokerButtonGame iconName={IconsEnum.LAPTOP_MAC} link={desktopLink![0]}>
              {labelAppDsk}
            </PokerButtonGame>
          )}
          {linkForAndroid && linkForIos && (
            <PokerButtonGame
              link={!isMobile ? { href: 'le-nostre-app' } : isIos ? linkForIos[0] : linkForAndroid[0]}
              iconName={IconsEnum.SMARTPHONE}
            >
              {labelAppMob}
            </PokerButtonGame>
          )}
        </div>
      </Wrapper>
    </React.Fragment>
  );
};
